import * as Yup from 'yup';

import {
  AuthenticateRequest,
  AuthenticateResponse,
  ForgotPasswordRequest,
  PasswordRequirements,
} from '../models';
import { urls } from '../utilities/dataHelpers';
import mediator, { MediatorRequestWithResponse } from '../utilities/mediator';
import { store } from '../utilities/store';
import { loginStatus } from '../reducers/auth/reducer';
import { initialize } from '../reducers/user/reducer';
import { clearSession, setSessionObject } from '../utilities/storage';
import i18n from '../utilities/i18n';
import { mapAccountEntityToAccount } from '../utilities/mappers';
import { ApiEndpoints, SystemCapabilities } from 'adc-sirius-module-common';
import {
  initialState,
  setCapabilities,
} from '../reducers/capabilities/reducer';

export const handleUserRefresh = (
  response: AuthenticateResponse,
): Promise<void> =>
  mediator
    .request<SystemCapabilities>('capability:getSystemCapabilities')
    .then((capabilities) => {
      store.dispatch(setCapabilities(capabilities));
      setSessionObject('user', { loggedIn: true });
      store.dispatch(
        initialize(
          response.user ? mapAccountEntityToAccount(response.user) : {},
        ),
      );
      store.dispatch(loginStatus(response.loggedIn || false));
    });

export const handleUserLoggedOut = () => {
  clearSession();
  store.dispatch(initialize({}));
  store.dispatch(loginStatus(false));
  store.dispatch(
    setCapabilities({
      ...initialState,
    }),
  );
};

export const generateYupPasswordSchema = (
  requirements: PasswordRequirements,
) => {
  let passwordSchema = Yup.object().shape({
    password: Yup.string().required(i18n.t('Form.passwordRequired')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], i18n.t('Form.passwordsDoNotMatch'))
      .required(i18n.t('Form.passwordRequired')),
  });

  const passwordRules = requirements.rules;

  for (const rule in passwordRules) {
    if (rule === 'min') {
      passwordSchema = passwordSchema.concat(
        Yup.object().shape({
          password: Yup.string().min(
            passwordRules[rule].value,
            i18n.t(passwordRules[rule].errorMsgKey, {
              count: passwordRules[rule].value,
            }),
          ),
        }),
      );
    } else if (rule === 'max') {
      passwordSchema = passwordSchema.concat(
        Yup.object().shape({
          password: Yup.string().max(
            passwordRules[rule].value,
            i18n.t(passwordRules[rule].errorMsgKey, {
              count: passwordRules[rule].value,
            }),
          ),
        }),
      );
    } else {
      passwordSchema = passwordSchema.concat(
        Yup.object().shape({
          password: Yup.string().test(
            rule,
            i18n.t(passwordRules[rule].errorMsgKey),
            (value) => {
              if (!value) {
                return true;
              }

              const regex = new RegExp(passwordRules[rule].value);

              return regex.test(value);
            },
          ),
        }),
      );
    }
  }

  return passwordSchema;
};

const authenticate = (
  request: MediatorRequestWithResponse<AuthenticateResponse>,
) => {
  request.response = new Promise<AuthenticateResponse>((resolve, reject) => {
    mediator
      .request<AuthenticateResponse>('api:invoke', {
        method: 'POST',
        url: urls.api + 'auth',
        data: request as AuthenticateRequest,
      })
      .then(async (result) => {
        if (result.error || result.status !== 200) {
          reject(result);
        } else {
          const response = result.data as AuthenticateResponse;

          await handleUserRefresh(response);

          resolve(response);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const forgotPassword = (request: MediatorRequestWithResponse<void>) => {
  request.response = new Promise<void>((resolve, reject) => {
    mediator
      .request<void>('api:invoke', {
        method: 'POST',
        url: urls.api + 'auth/forgot',
        data: request as ForgotPasswordRequest,
      })
      .then((result) => {
        if (result.status === 200) {
          resolve();
          return;
        }

        reject(new Error('Errors_auth.forgotPasswordUnexpected'));
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const logout = () => {
  // fire off the api call to sign out
  mediator.request<any>('api:invoke', {
    method: 'GET',
    url: urls.api + 'auth/signout',
  });
  handleUserLoggedOut();
};

const verifyEmail = (request: MediatorRequestWithResponse<void>) => {
  const url = `${urls.api}${ApiEndpoints.EP_AUTH_VERIFY_EMAIL.Path}`;

  const queryParams = new URLSearchParams({
    token: request.data.token,
  });

  request.response = new Promise<void>((resolve, reject) => {
    mediator
      .request<any>('api:invoke', {
        method: ApiEndpoints.EP_AUTH_VERIFY_EMAIL.Method,
        url: `${url}?${queryParams.toString()}`,
      })
      .then((result) => {
        if (result.status === 200) {
          resolve();
          return;
        }

        const message = result.data.error?.message;

        // TODO: CSP-2673 Replace with error code
        if (
          message?.includes('invalidOrExpiredToken') ||
          message?.includes('invalidTokenAccount')
        ) {
          reject(new Error('Auth_VerifyEmail.error.messageInvalidToken'));
        }

        reject(new Error('Auth_VerifyEmail.error.messageUnexpected'));
      })
      .catch((err) => {
        reject(err);
      });
  });
};

mediator.subscribe('auth:authenticate', authenticate);
mediator.subscribe('auth:forgotPassword', forgotPassword);
mediator.subscribe('auth:logout', logout);
mediator.subscribe('user:loggedOut', handleUserLoggedOut);
mediator.subscribe('auth:verifyEmail', verifyEmail);
